// Draft email produced by Dreamer. Delivered on the chat message as `emailDraft`
// (see MessagePayload/ChatMessage) and rendered by DraftEmailCard.
//
// The shape mirrors the messaging payload one-for-one. Versions accumulate on a
// single draft via the in-card Retry action; a typed follow-up message is an
// ordinary chat turn that produces its own new draft.
// Sending is client-side only — the card opens the user's email app pre-filled;
// the backend never sends.

// Channels the user can open the draft in. gmail/outlook open the provider's
// web compose; mailto opens the OS default mail client.
export type EmailProvider = "gmail" | "outlook";
export type SendChannel = EmailProvider | "mailto";

// Single source of truth for the send channels — used by the send button (which
// offers them) and the default-provider hook (which validates stored values).
export const SEND_CHANNELS: SendChannel[] = ["gmail", "outlook", "mailto"];

// A recipient identified by conversational's Activities Cruncher flow. No email
// address travels on this object — it is CRM identity metadata (which person,
// how confidently resolved), addressed to an email via personId through
// usePeople. A recipient with no personId (name-only, reducer_inferred) can't be
// addressed and is dropped rather than guessed.
export type RecipientResolutionSource =
  | "search_direct"
  | "search_role"
  | "reducer_inferred";

export interface DraftEmailRecipient {
  personId?: string | null;
  fullName?: string | null;
  title?: string | null;
  resolutionSource?: RecipientResolutionSource | null;
  person_id?: string | null;
  full_name?: string | null;
  resolution_source?: RecipientResolutionSource | null;
}

// The generated draft itself, round-tripped verbatim through messaging from the
// service that produced it. Citations and the transparency note ride along on
// the same object but are not rendered yet.
//
// Both key casings are declared because the blob is stored and re-emitted as an
// opaque JSON column: nothing in messaging normalizes it, so the casing is
// whichever the producer dumped. Read it through draftSubject/draftBody/
// draftRecipients rather than reaching for a field directly.
export interface DraftEmailContent {
  emailSubject?: string;
  emailBody?: string;
  email_subject?: string;
  email_body?: string;
  recipients?: DraftEmailRecipient[];
}

export interface DraftEmailVersion {
  // 1-based version number, matching the value selectedVersion points at.
  version: number;
  draft: DraftEmailContent;
  // The retry instruction that produced this version; absent on a first draft.
  guidance?: string | null;
  // ISO 8601 timestamp; formatted for display in the user's local time.
  createdAt?: string | null;
}

export interface DraftEmailPayload {
  draftId: string;
  // Which version the card shows — a version number, NOT an index into versions.
  selectedVersion: number;
  // Ordered oldest → newest. Length > 1 renders the version navigator.
  versions: DraftEmailVersion[];
  // Set once the draft has been sent. Read-only: nothing records a send yet.
  sentVersion?: number | null;
  sentVia?: string | null;
  sentAt?: string | null;
}

export const draftSubject = (version: DraftEmailVersion | undefined): string =>
  version?.draft?.emailSubject ?? version?.draft?.email_subject ?? "";

export const draftBody = (version: DraftEmailVersion | undefined): string =>
  version?.draft?.emailBody ?? version?.draft?.email_body ?? "";

export const draftRecipients = (
  version: DraftEmailVersion | undefined
): DraftEmailRecipient[] => version?.draft?.recipients ?? [];

// Recipient personIds usable as a usePeople lookup — deduped, name-only
// recipients (no personId to resolve) excluded.
export const draftRecipientPersonIds = (
  version: DraftEmailVersion | undefined
): string[] => {
  const recipientIds = draftRecipients(version)
    .map((recipient) => recipient.personId ?? recipient.person_id)
    .filter((id): id is string => !!id);
  return Array.from(new Set(recipientIds));
};

// The version the card is showing, or the newest one when selectedVersion points
// at a version that is not in the list (a payload trimmed server-side).
export const selectedDraftVersion = (
  payload: DraftEmailPayload
): DraftEmailVersion | undefined =>
  payload.versions.find((version) => version.version === payload.selectedVersion) ??
  payload.versions[payload.versions.length - 1];

// Whether the card has something to show. Rendering the card replaces the
// markdown message body (which carries the same email for channels without a
// card), so a draft with no body must not qualify.
export const hasRenderableDraft = (
  payload: DraftEmailPayload | undefined
): boolean =>
  !!payload && draftBody(selectedDraftVersion(payload)).trim().length > 0;
